'use client';

import { DashboardData, ProcessedNotification } from '@/types/api';
import NotificationItem from './notification-item';

const MoreIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
  </svg>
);

interface NotificationPanelProps {
  data: DashboardData;
}

export default function NotificationPanel({ data }: NotificationPanelProps) {
  const generateNotifications = (): ProcessedNotification[] => {
    const now = new Date();

    return (data?.expired_vessels || [])
      .slice(0, 8)
      .map((vessel, index) => {
        const expireDate = new Date(vessel.expire_date);
        const diffInDays = Math.ceil(
          (expireDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        );

        let severity: 'high' | 'medium' | 'low' = 'low';
        let type = 'Certificate Expiring';
        let time = `${Math.abs(diffInDays)} days`;

        if (diffInDays < 0) {
          severity = 'high';
          type = 'Vessel Expired';
          time = `${Math.abs(diffInDays)} days ago`;
        } else if (diffInDays <= 7) {
          severity = 'high';
          type = 'Vessel Expiring Soon';
          time = `in ${diffInDays} days`;
        } else if (diffInDays <= 30) {
          severity = 'medium';
          type = 'Vessel Expiring';
          time = `in ${diffInDays} days`;
        }

        return {
          id: index + 1,
          vessel: vessel.vessel_name,
          type,
          time,
          severity,
          expireDate: vessel.expire_date,
          is_hidden: vessel.is_hidden,
        };
      })
      .sort((a, b) => {
        const severityOrder = { high: 0, medium: 1, low: 2 };
        return severityOrder[a.severity] - severityOrder[b.severity];
      });
  };

  const notifications = generateNotifications();

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm h-full flex flex-col justify-between p-5">
      <div className="flex items-start justify-between pb-3 border-b border-gray-100 dark:border-gray-800">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white tracking-tight">Latest Expired Vessels</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Vessels with expiring or expired certificates</p>
        </div>
        <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200">
          <MoreIcon />
        </button>
      </div>

      <div className="mt-2 flex-1 overflow-y-auto max-h-[250px] pr-1">
        {notifications.length === 0 ? (
          <div className="py-8 text-center text-xs text-gray-400">
            Tidak ada kapal dengan sertifikat kedaluwarsa
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {notifications.map((notification, index) => (
              <NotificationItem key={notification.id} notification={notification} index={index} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
